import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Modal from 'components/ui/modal';
import Form from 'components/form';
import Input from 'components/form/Input';
import DropZone from 'components/form/Dropzone';
import Button from 'components/common/Button';
import useSnack from 'hooks/useSnack';
import { API, Storage } from 'aws-amplify';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import { useMutation } from 'react-apollo';
import { UPDATE_DOCUMENT } from 'graphql/student/essays';
import { ADD_PERSONAL_STATEMENT_ESSAY, GET_STATEMENTS } from 'graphql/essays';

const FormItem = Form.FormItem;

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: 360,
    [theme.breakpoints.down('sm')]: {
      minWidth: 30,
    },
  },
  input: {
    marginBottom: theme.spacing(3),
  },
}));

const AddDocument = ({ onClose, form, user, doc, statement, matches }) => {
  const classes = useStyles();
  const [files, setFiles] = useState([]);
  const snack = useSnack();
  const [addDocument] = useMutation(ADD_PERSONAL_STATEMENT_ESSAY);
  const [updateDocument] = useMutation(UPDATE_DOCUMENT);
  const { getFieldDecorator, validateFields } = form;

  const handleSubmit = () => {
    return validateFields().then(async (values) => {
      try {
        if (doc?.id) {
          await updateDocument({
            variables: {
              id: doc.id,
              data: values
            }
          });
          snack.success('Document updated');
        } else {
          let fileResp;
          if (files.length) {
            fileResp = await upload(files[0], user.email);
          } else {
            fileResp = await createDoc(values.filename, user.email);
          }
          // variable values filtered with property "file" as new_values
          const new_values = Object.keys(values).filter(key => key !== 'file').reduce((obj, key) => {
            obj[key] = values[key];
            return obj;
          },{})
          await addDocument({
            variables: {
              data: {
                personal_statements_id: statement.id,
                document:{
                  data:{
                    created_by: user.id,
                    document_id: fileResp.id,
                    document_storage: 'gdrive',
                    url: fileResp.webViewLink,
                    status: 'new',
                    ...new_values,
                  }
                }
              }
            },
            update: (proxy,mutationResult) => {
              const data = proxy.readQuery({
                query: GET_STATEMENTS,
                variables: {
                  userId: user.id,
                },
              });
              statement.documents.push(mutationResult.data.row);
              proxy.writeQuery({
                query: GET_STATEMENTS,
                variables: {
                  userId: user.id,
                },
                data: {
                  rows: data.rows.map(i => i.id === statement.id ? statement : i),
                },
              });
            }
          });
          snack.success('Document created');
        }
        onClose();
      } catch (e) {
        snack.error(e.message);
      }
    });
  };

  const handleFileSelect = (f) => {
    setFiles(f);
  };

  const upload = async (file, ownerEmail) => {
    const { key } = await Storage.put(file.name, file, {
      level: 'public',
    });
    const url = `https://weadmit-user-pics-bucket.s3.us-east-2.amazonaws.com/public/${key}`;

    const resp = await API.post('api', '/document', {
      body: {
        url,
        ownerEmail
      },
    });
    await Storage.remove(key);
    return resp;
  };

  const createDoc = async (name, ownerEmail) => {
    return  await API.post('api', '/document/new', {
      body: {
        name,
        ownerEmail
      },
    });
  };

  return (
    <Modal
      open
      fullWidth
      title={doc?.id ? 'Update Essay' : "Add Essay"}
      size={"md"}
      onClose={onClose}
      actions={[
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>,
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          {doc?.id ? 'Update Essay' : "Add Essay"}
        </Button>,
      ]}
    >
      <div className={classes.root}>
        <FormItem>
          {getFieldDecorator('filename', {
            initialValue: doc?.filename,
            rules: [{ required: true }],
          })(<Input label="Document name" disabled={files.length} />)}
        </FormItem>
        <br />
        { !doc?.id && (
          <>
            <Typography variant="caption" style={{ display: 'flex' }}>
              <InfoIcon fontSize={'small'} />
              &nbsp;If you’ve already started working on your essay, you may upload the file here. Otherwise, a new Google Document will be created automatically for you.
            </Typography>
            <div>
              <FormItem>
                {getFieldDecorator('file')(
                  <DropZone
                    accept=".doc,.docx"
                    onUpload={handleFileSelect}
                    attachments={files}
                  />,
                )}
              </FormItem>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default Form.create()(AddDocument);
